import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { computeCourierMeasures } from "../../utils/courierControlPure";
import { searchMuestrasCourier } from "../../api/CourierControl/courierControlApi";
import { pageFieldSx } from "../../styles/pageModernStyles";

export const emptyLine = (partial = {}) => ({
  Temporada: "",
  Marca: "",
  Estilo: "",
  TipoPrenda: "",
  PesoKg: "",
  Tejido: "",
  Color: "",
  Talla: "",
  TipoMuestra: "",
  CodigoMuestra: "",
  IdMuestra: null,
  IdMovimientoMuestra: null,
  Motivo: "",
  ...partial,
});

export const emptyEnvio = (partial = {}) => ({
  Direccion: "SALIDA",
  Ambito: "NACIONAL",
  Fecha: new Date().toISOString().slice(0, 10),
  SolicitadoPor: "",
  Origen: "",
  Destino: "",
  Contacto: "",
  TipoEmbalaje: "",
  LargoCm: "",
  AnchoCm: "",
  AltoCm: "",
  PesoBrutoKg: "",
  CompaniaCourier: "",
  NumeroAwb: "",
  Coste: "",
  CosteReal: "",
  Pais: "",
  Estado: "PEDIDO",
  Notas: "",
  lineas: [emptyLine()],
  ...partial,
});

const fmt = (value, digits = 3) => {
  if (value == null || value === "") return "—";
  return Number(value).toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: "block",
      fontWeight: 800,
      letterSpacing: 0.8,
      color: "text.secondary",
      mb: 1,
      mt: 0.5,
    }}
  >
    {children}
  </Typography>
);

function SampleCodeField({ value, onChange, fieldSx }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const q = String(value.CodigoMuestra || "").trim();
    if (q.length < 2) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const rows = await searchMuestrasCourier(q);
        if (!cancelled) setOptions(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setOptions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value.CodigoMuestra]);

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      filterOptions={(opts) => opts}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.MUESTRA || ""
      }
      inputValue={value.CodigoMuestra || ""}
      onInputChange={(_, next, reason) => {
        if (reason === "reset") return;
        onChange({
          ...value,
          CodigoMuestra: next,
          IdMuestra: null,
        });
      }}
      onChange={(_, next) => {
        if (next && typeof next === "object") {
          onChange({
            ...value,
            CodigoMuestra: next.MUESTRA || "",
            IdMuestra: next.ID_MUESTRA,
          });
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("courierControl.sampleCode")}
          sx={fieldSx}
          helperText={
            value.IdMovimientoMuestra
              ? t("courierControl.linkedFromShipment")
              : value.IdMuestra
                ? t("courierControl.linkedSampleOnly")
                : t("courierControl.sampleHint")
          }
        />
      )}
    />
  );
}

function PieceCard({
  linea,
  index,
  onChange,
  onRemove,
  canRemove,
  requestDisabled,
  fieldSx,
  t,
}) {
  const theme = useTheme();
  const title =
    String(linea.CodigoMuestra || "").trim() ||
    String(linea.Estilo || "").trim() ||
    `${t("courierControl.piece")} ${index + 1}`;
  const hasDetails = Boolean(
    linea.Temporada ||
    linea.Marca ||
    linea.TipoPrenda ||
    linea.Tejido ||
    linea.Color ||
    linea.Talla ||
    linea.TipoMuestra ||
    linea.PesoKg,
  );
  const [openDetails, setOpenDetails] = useState(hasDetails);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.85),
        borderRadius: 2,
        bgcolor: alpha(theme.palette.background.paper, 0.9),
        overflow: "hidden",
        mb: 1.25,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderBottom: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.55),
        }}
      >
        <Typography
          sx={{ fontWeight: 800, fontSize: "0.9rem", flex: 1 }}
          noWrap
        >
          {title}
        </Typography>
        {linea.IdMovimientoMuestra ? (
          <Chip
            size="small"
            label={t("courierControl.fromMatrixShipment")}
            sx={{ height: 24, fontWeight: 700 }}
            color="primary"
            variant="outlined"
          />
        ) : linea.IdMuestra ? (
          <Chip
            size="small"
            label={t("courierControl.sampleLinkOnly")}
            sx={{ height: 24, fontWeight: 700 }}
            color="warning"
            variant="outlined"
          />
        ) : null}
        <IconButton
          size="small"
          onClick={() => setOpenDetails((v) => !v)}
          aria-label={t("courierControl.pieceDetails")}
        >
          {openDetails ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
        {canRemove ? (
          <IconButton
            size="small"
            aria-label={t("courierControl.deleteLine")}
            onClick={onRemove}
            disabled={requestDisabled}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>

      <Box sx={{ p: 1.5 }}>
        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={6}>
            <SampleCodeField
              value={linea}
              fieldSx={fieldSx}
              onChange={onChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.style")}
              value={linea.Estilo || ""}
              disabled={requestDisabled}
              sx={fieldSx}
              onChange={(e) => onChange({ ...linea, Estilo: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.reason")}
              value={linea.Motivo || ""}
              disabled={requestDisabled}
              sx={fieldSx}
              onChange={(e) => onChange({ ...linea, Motivo: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t("courierControl.lineWeight")}
              value={linea.PesoKg ?? ""}
              disabled={requestDisabled}
              sx={fieldSx}
              onChange={(e) => onChange({ ...linea, PesoKg: e.target.value })}
            />
          </Grid>
        </Grid>

        <Collapse in={openDetails}>
          <Grid container spacing={1.25} sx={{ mt: 0.25 }}>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.season")}
                value={linea.Temporada || ""}
                disabled={requestDisabled}
                sx={fieldSx}
                onChange={(e) =>
                  onChange({ ...linea, Temporada: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.brand")}
                value={linea.Marca || ""}
                disabled={requestDisabled}
                sx={fieldSx}
                onChange={(e) => onChange({ ...linea, Marca: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.garmentType")}
                value={linea.TipoPrenda || ""}
                disabled={requestDisabled}
                sx={fieldSx}
                onChange={(e) =>
                  onChange({ ...linea, TipoPrenda: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.sampleType")}
                value={linea.TipoMuestra || ""}
                disabled={requestDisabled}
                sx={fieldSx}
                onChange={(e) =>
                  onChange({ ...linea, TipoMuestra: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.fabric")}
                value={linea.Tejido || ""}
                disabled={requestDisabled}
                sx={fieldSx}
                onChange={(e) => onChange({ ...linea, Tejido: e.target.value })}
              />
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.color")}
                value={linea.Color || ""}
                disabled={requestDisabled}
                sx={fieldSx}
                onChange={(e) => onChange({ ...linea, Color: e.target.value })}
              />
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.size")}
                value={linea.Talla || ""}
                disabled={requestDisabled}
                sx={fieldSx}
                onChange={(e) => onChange({ ...linea, Talla: e.target.value })}
              />
            </Grid>
          </Grid>
        </Collapse>
      </Box>
    </Box>
  );
}

export default function CourierControlDialog({
  open,
  envio,
  saving,
  onClose,
  onChange,
  onSave,
  onDelete,
  canSolicitar = false,
  canTerminar = false,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fieldSx = pageFieldSx(theme);
  const isNew = !envio.IdCourierControl;
  const requestDisabled = isNew ? !canSolicitar : !canTerminar;
  const finishDisabled = !canTerminar;
  const canSave = isNew ? canSolicitar : canTerminar;
  const canDelete = Boolean(envio.IdCourierControl) && canTerminar;
  const measures = useMemo(
    () =>
      computeCourierMeasures({
        largoCm: envio.LargoCm,
        anchoCm: envio.AnchoCm,
        altoCm: envio.AltoCm,
        pesoBrutoKg: envio.PesoBrutoKg,
        coste: envio.Coste,
        costeReal: envio.CosteReal,
      }),
    [envio],
  );

  const setField = (field, value) => onChange({ ...envio, [field]: value });

  const setLine = (index, next) => {
    const lineas = envio.lineas.map((linea, i) => (i === index ? next : linea));
    onChange({ ...envio, lineas });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          overflow: "hidden",
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.8),
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 1.75,
          px: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.background.paper, 0.98),
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>
          {envio.IdCourierControl
            ? t("courierControl.editTitle")
            : t("courierControl.newRequest")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.35, fontWeight: 500, maxWidth: 640 }}
        >
          {t("courierControl.dialogSubtitle")}
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.default, 0.35)
              : alpha(theme.palette.grey[50], 0.65),
          ...(requestDisabled
            ? {
                "& .cc-request .MuiInputBase-root": {
                  pointerEvents: "none",
                  opacity: 0.72,
                },
              }
            : null),
          ...(finishDisabled
            ? {
                "& .cc-finish .MuiInputBase-root": {
                  pointerEvents: "none",
                  opacity: 0.72,
                },
              }
            : null),
        }}
      >
        <Box className="cc-request">
          <SectionLabel>{t("courierControl.sectionRoute")}</SectionLabel>
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                size="small"
                label={t("courierControl.direction")}
                value={envio.Direccion}
                disabled={requestDisabled}
                sx={fieldSx}
                onChange={(e) => setField("Direccion", e.target.value)}
              >
                <MenuItem value="SALIDA">{t("courierControl.exit")}</MenuItem>
                <MenuItem value="ENTRADA">{t("courierControl.entry")}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                size="small"
                label={t("courierControl.scope")}
                value={envio.Ambito}
                sx={fieldSx}
                onChange={(e) => setField("Ambito", e.target.value)}
              >
                <MenuItem value="NACIONAL">
                  {t("courierControl.national")}
                </MenuItem>
                <MenuItem value="INTERNACIONAL">
                  {t("courierControl.international")}
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t("courierControl.date")}
                InputLabelProps={{ shrink: true }}
                value={String(envio.Fecha || "").slice(0, 10)}
                sx={fieldSx}
                onChange={(e) => setField("Fecha", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3} className="cc-finish">
              <TextField
                select
                fullWidth
                size="small"
                label={t("courierControl.status")}
                value={envio.Estado || "PEDIDO"}
                disabled={finishDisabled}
                sx={fieldSx}
                onChange={(e) => setField("Estado", e.target.value)}
              >
                <MenuItem value="PEDIDO">
                  {t("courierControl.statusPedido")}
                </MenuItem>
                <MenuItem value="EN_CURSO">
                  {t("courierControl.statusEnCurso")}
                </MenuItem>
                <MenuItem value="ENTREGADO">
                  {t("courierControl.statusEntregado")}
                </MenuItem>
                <MenuItem value="CANCELADO">
                  {t("courierControl.statusCancelado")}
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.origin")}
                value={envio.Origen || ""}
                sx={fieldSx}
                onChange={(e) => setField("Origen", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.destination")}
                value={envio.Destino || ""}
                sx={fieldSx}
                onChange={(e) => setField("Destino", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.contact")}
                value={envio.Contacto || ""}
                sx={fieldSx}
                onChange={(e) => setField("Contacto", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.requestedBy")}
                value={envio.SolicitadoPor || ""}
                sx={fieldSx}
                onChange={(e) => setField("SolicitadoPor", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.country")}
                value={envio.Pais || ""}
                sx={fieldSx}
                onChange={(e) => setField("Pais", e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2.25 }} />

        <Box className="cc-finish">
          <SectionLabel>{t("courierControl.sectionBox")}</SectionLabel>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.packing")}
                value={envio.TipoEmbalaje || ""}
                disabled={finishDisabled}
                sx={fieldSx}
                onChange={(e) => setField("TipoEmbalaje", e.target.value)}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.length")}
                value={envio.LargoCm ?? ""}
                disabled={finishDisabled}
                sx={fieldSx}
                onChange={(e) => setField("LargoCm", e.target.value)}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.width")}
                value={envio.AnchoCm ?? ""}
                disabled={finishDisabled}
                sx={fieldSx}
                onChange={(e) => setField("AnchoCm", e.target.value)}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.height")}
                value={envio.AltoCm ?? ""}
                disabled={finishDisabled}
                sx={fieldSx}
                onChange={(e) => setField("AltoCm", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.grossWeight")}
                value={envio.PesoBrutoKg ?? ""}
                disabled={finishDisabled}
                sx={fieldSx}
                onChange={(e) => setField("PesoBrutoKg", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.courier")}
                value={envio.CompaniaCourier || ""}
                disabled={finishDisabled}
                sx={fieldSx}
                onChange={(e) => setField("CompaniaCourier", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.awb")}
                value={envio.NumeroAwb || ""}
                disabled={finishDisabled}
                sx={fieldSx}
                onChange={(e) => setField("NumeroAwb", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.cost")}
                value={envio.Coste ?? ""}
                disabled={finishDisabled}
                sx={fieldSx}
                onChange={(e) => setField("Coste", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.realCost")}
                value={envio.CosteReal ?? ""}
                disabled={finishDisabled}
                sx={fieldSx}
                onChange={(e) => setField("CosteReal", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction="row"
                flexWrap="wrap"
                useFlexGap
                spacing={1}
                sx={{
                  px: 1.5,
                  py: 1.1,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                }}
              >
                <Chip
                  size="small"
                  label={`${t("courierControl.volume")}: ${fmt(measures.volumenM3, 6)} m³`}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  label={`${t("courierControl.volWeight")}: ${fmt(measures.pesoVolumetricoKg)} kg`}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  label={`${t("courierControl.chargeable")}: ${fmt(measures.pesoFacturableKg)} kg`}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  label={`${t("courierControl.difference")}: ${
                    measures.diferencia == null
                      ? "—"
                      : `${fmt(measures.diferencia, 2)} €`
                  }`}
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2.25 }} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <SectionLabel>{t("courierControl.sectionPieces")}</SectionLabel>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={requestDisabled}
            onClick={() =>
              onChange({ ...envio, lineas: [...envio.lineas, emptyLine()] })
            }
            sx={{ fontWeight: 700, textTransform: "none" }}
          >
            {t("courierControl.addLine")}
          </Button>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.25 }}
        >
          {t("courierControl.piecesHint")}
        </Typography>

        {(envio.lineas || []).map((linea, index) => (
          <PieceCard
            key={
              linea.IdCourierControlLinea ||
              `${linea.IdMuestra || "x"}-${index}`
            }
            linea={linea}
            index={index}
            fieldSx={fieldSx}
            t={t}
            requestDisabled={requestDisabled}
            canRemove={!requestDisabled && (envio.lineas || []).length > 0}
            onChange={(next) => setLine(index, next)}
            onRemove={() =>
              onChange({
                ...envio,
                lineas:
                  envio.lineas.length === 1
                    ? [emptyLine()]
                    : envio.lineas.filter((_, i) => i !== index),
              })
            }
          />
        ))}

        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label={t("courierControl.notes")}
          value={envio.Notas || ""}
          sx={{ ...fieldSx, mt: 1 }}
          onChange={(e) => setField("Notas", e.target.value)}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          justifyContent: "space-between",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          {canDelete ? (
            <Button
              color="error"
              onClick={onDelete}
              disabled={saving}
              sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
            >
              {t("courierControl.delete")}
            </Button>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {t("courierControl.cancel")}
          </Button>
          {canSave ? (
            <Button
              variant="contained"
              onClick={onSave}
              disabled={saving}
              sx={{
                borderRadius: 2,
                fontWeight: 800,
                textTransform: "none",
                px: 2.5,
              }}
            >
              {t("courierControl.save")}
            </Button>
          ) : null}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
